import { Integration, Product } from '@futrli-sdk/app-store';
import { BillingModel, DataSource, Organisation, ProductSetup } from '@futrli-sdk/organisations';
import { get } from 'lodash';
import { v4 as uuid } from 'uuid';
import { DATASOURCES_STATUS } from '../../app-core/constants';
import { isConnected } from './datasources-status-utils';

/**
 * @deprecated
 * Gets all datasources in all enabled products.
 *
 * @param {Organisation} organisation
 * @param {string} productId
 * @returns {DataSource[]}
 */
export const getEnabledDatasources = (organisation: Organisation): DataSource[] => [organisation.datasource];

/**
 * Gets the datasource from an org if it is connected.
 */
export const getConnectedDatasource = (org: Organisation) => {
  return org.datasource && isConnected(org.datasource) ? org.datasource : undefined;
};

/**
 * Adds a given product to the organisation, if it doesn't already exist.
 *
 * @param {Partial<Organisation>} organisation
 * @param {Product} product
 * @returns {Partial<Organisation>}
 */
export const addProductToOrganisation = (
  organisation: Partial<Organisation>,
  product: ProductSetup
): Partial<Organisation> => {
  const enabledProducts = organisation.enabled_products || [];

  return Object.assign({}, organisation, {
    enabled_products: enabledProducts.concat(product)
  });
};

/**
 * Checks if a given product is in enabled_products
 *
 * @param {Partial<Organisation>} organisation
 * @param {Product} product
 * @returns {boolean}
 */
export const isProductEnabled = (organisation: Partial<Organisation>, product: Product): boolean =>
  Boolean(getProductFromOrganisation(organisation, product));

/**
 * Finds the current product within the organisations enabled products.
 *
 * @param {Partial<Organisation>} organisation
 * @param {Product} product
 * @returns {ProductSetup}
 */
export const getProductFromOrganisation = (
  organisation: Partial<Organisation>,
  product: Product
): ProductSetup | undefined => {
  const enabledProducts = get(organisation, 'enabled_products') || [];

  return enabledProducts.find((p: ProductSetup) => p.product_id === product.id);
};

/**
 * Update billing model in a product
 * @param {ProductSetup} product
 * @param {BillingModel} billing_model
 * @returns {ProductSetup}
 */
export const updateBillingModelInProduct = (product: ProductSetup, billing_model: BillingModel): ProductSetup =>
  Object.assign({}, product, {
    billing_model
  });

/**
 * When the user selects a product we don't have all the info yet, make a default
 * ProductSetup object.
 *
 * @param {Product} product
 * @returns {ProductSetup}
 */
export const instantiateProductSetup = (product: Product): ProductSetup => ({
  id: uuid(),
  created: '1970-01-01T00:00:00.000Z',
  product_id: product.id,
  name: product.name,
  image: product.image,
  billing_model: 'free',
  launch_url: product.name,
  is_pro: false
});

/**
 * When a user selects an integration type we need to instantiate a new DataSource
 * object.
 *
 * @param {Integration} integration
 * @returns {DataSource}
 */
export const instantiateDataSource = (integration: Integration): DataSource => ({
  id: uuid(),
  integration_id: integration.id,
  currency_code: '',
  parent_datasource_id: '',
  type: integration.name.toLowerCase(),
  name: integration.name,
  status: {
    value: DATASOURCES_STATUS.never_connected,
    description: integration.description,
    created: Date.now().toString(),
    modified: Date.now().toString()
  },
  sync_history: [],
  image: integration.image
});

/**
 * Instantiates a datasource given an specific integration.
 * @param {string} integrationId
 * @param {Integration[]} integrations
 * @returns {DataSource|undefined}
 * @throws integration not found
 */
export const createDatasourceByIntegration = (
  integrationId: string,
  integrations: Integration[]
): DataSource | never => {
  const integration = integrations.find((i: Integration) => i.id === integrationId);

  if (!integration) {
    throw new Error(`Integration not found with id ${integrationId}`);
  }

  return instantiateDataSource(integration);
};

/**
 * Find a given product setup by slug in products listing and then in
 * an organisation's enabled products.
 *
 * @param {Organisation} organisation
 * @param {Product[]} products
 * @param {string} slug
 * @returns {ProductSetup}
 */
export const findProductSetupById = (organisation: Organisation, productId: string): ProductSetup => {
  const productSetup = organisation.enabled_products.find(setup => setup.product_id === productId);

  if (!productSetup) {
    throw new Error(`ProductSetup associated with product ID "${productId}" not found on organisation.`);
  }

  return productSetup;
};

export const findProductSetupBySlug = (organisation: Organisation, products: Product[], slug: string): ProductSetup => {
  const product = products.find((ps: Product) => ps.slug === slug);

  if (!product) {
    throw new Error(`Product with slug "${slug}" not found from app store.`);
  }

  const productSetup = organisation.enabled_products.find((ps: ProductSetup) => ps.product_id === product.id);

  if (!productSetup) {
    throw new Error(`ProductSetup associated with slug "${slug}" not found on organisation.`);
  }

  return productSetup;
};
